import { useEffect, useState } from 'react';
import useApi from 'hooks/useApi';
import { AnnouncementAttachment } from 'types/auth';

// What the API is supposed to hand us. `url` is minted per read and lives 12h;
// `public_url` only works if the bucket is publicly readable.
export const storedSrc = (attachment: AnnouncementAttachment) => attachment?.url || attachment?.public_url || '';

/**
 * Fills in attachment URLs the API did not supply.
 *
 * `withSignedUrls` on the server swallows a signing failure per object and sets
 * `url: null`, and the upload-time URL expires after 600s — so an attachment can reach
 * the UI with nothing usable to render. Anything with a `file_path` can still be
 * recovered by re-signing it here, which is what this does.
 *
 * Returns a resolver: stored URL first, freshly-minted one as the fallback, empty string
 * when the record has no `file_path` at all (nothing can be done for those).
 */
const useAttachmentUrls = (attachments: AnnouncementAttachment[] = []) => {
  const { getAssetSignedUrl } = useApi();
  const [resolved, setResolved] = useState<Record<string, string>>({});

  // Identity of the set of paths still needing a URL, so the effect re-runs when the
  // announcement changes but not on every render.
  const missing = attachments
    .filter((attachment) => attachment?.file_path && !storedSrc(attachment))
    .map((attachment) => attachment.file_path);
  const missingKey = missing.join('|');

  useEffect(() => {
    if (!missingKey) return;
    let active = true;

    (async () => {
      const entries = await Promise.all(
        missingKey.split('|').map(async (file_path) => {
          try {
            const response = await getAssetSignedUrl(file_path);
            return [file_path, response?.data?.url ?? ''] as const;
          } catch {
            // One unsigned object must not take down the rest of the gallery.
            return [file_path, ''] as const;
          }
        })
      );
      if (!active) return;
      setResolved((prev) => ({ ...prev, ...Object.fromEntries(entries.filter(([, url]) => url)) }));
    })();

    return () => {
      active = false;
    };
  }, [missingKey, getAssetSignedUrl]);

  return (attachment: AnnouncementAttachment) =>
    storedSrc(attachment) || (attachment?.file_path ? resolved[attachment.file_path] ?? '' : '');
};

export default useAttachmentUrls;
